import React, { useState, useEffect, useCallback } from "react";
import { IEventSummary, IMoveUpSummary } from "@eagerdog/interfaces";
import { Constants } from "@eagerdog/constants";
import moment from "moment";

import { apiService } from "src/services/api.service";

import type { ColDef } from "ag-grid-community";
import { themeQuartz } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";

import { ActionsRenderer } from "./ActionsRenderer";
import { StatusRenderer } from "./StatusRenderer";

import styles from "./MoveUpsTab.module.scss";
import Input from "src/components/Input/Input";
import Button from "src/components/Button/Button";
import { GridApi } from "ag-grid-community";
import { useDebounce } from "src/hooks/useDebounce";

interface IProps {
  event: IEventSummary;
  currentTab: number;
  tabIndex: number;
}

interface IRow {
  date: string;
  callName: string;
  armNumber: string;
  showType: string;
  showName: string;
  showDate: string;
  from: string;
  to: string;
  status: string;
  createdBy: string;
  actions: {
    moveUp: IMoveUpSummary;
    event: IEventSummary;
    getMoveUps: () => void;
  };
}

const MoveUpsTab: React.FC<IProps> = (props) => {
  const [loaded, setLoaded] = useState<boolean>(false);
  const [gridApi, setGridApi] = useState<GridApi<IRow>>();
  const [dogNameSearch, setDogNameSearch] = useState<string>("");
  const debouncedDogNameSearch = useDebounce<string>(dogNameSearch, 800);
  const [rowData, setRowData] = useState<IRow[]>([]);

  const resetFilters = useCallback(() => {
    if (gridApi) {
      setDogNameSearch("");
      gridApi.setFilterModel(null);
    }
  }, [gridApi]);

  useEffect(() => {
    if (gridApi && debouncedDogNameSearch) {
      gridApi.setFilterModel({
        callName: {
          filterType: "text",
          type: "contains",
          filter: debouncedDogNameSearch,
        },
      });
    } else if (gridApi) {
      gridApi.setFilterModel(null);
    }
  }, [gridApi, debouncedDogNameSearch]);

  const theme = themeQuartz.withParams({
    fontFamily: "Roboto",
    headerFontFamily: "Roboto",
    cellFontFamily: "Roboto",
  });

  // Show types that use sections
  const sectionShowTypes = [
    Constants.show_type.nosework,
    Constants.show_type.herding,
    Constants.show_type.country_rat_race,
    Constants.show_type.barn_hunt,
  ];

  const formatClass = (element: string, level: string, section?: string, showType?: string) => {
    const parts = [element];
    if (level) parts.push(level);
    // Only show section for specific show types and when section is not "None"
    if (section && section !== "None" && showType && sectionShowTypes.includes(showType)) {
      parts.push(`(${section})`);
    }
    return parts.join(" - ");
  };

  const getMoveUps = useCallback(() => {
    apiService.getEventMoveUps(props.event._id).then((response) => {
      const rows: IRow[] = response.map((moveUp: IMoveUpSummary) => ({
        date: moment(moveUp.created_at).format("MM/DD/YY hh:mm A"),
        callName: moveUp.call_name || "Unknown",
        armNumber: moveUp.arm_number || "-",
        showType: moveUp.show_type || "-",
        showName: moveUp.show_name || "-",
        showDate: moveUp.show_date ? moment(moveUp.show_date).format("MMM Do") : "-",
        from: formatClass(moveUp.from_element, moveUp.from_level, moveUp.from_section, moveUp.show_type),
        to: formatClass(moveUp.to_element, moveUp.to_level, moveUp.to_section, moveUp.show_type),
        status: moveUp.status,
        createdBy: moveUp.created_by_name || "-",
        actions: {
          moveUp,
          event: props.event,
          getMoveUps: getMoveUps,
        },
      }));

      setRowData(rows);
    });
  }, [props.event]);

  const colDefs: ColDef<IRow>[] = [
    {
      field: "date",
      headerName: "Date",
      width: 160,
      minWidth: 160,
      maxWidth: 180,
      filter: true,
    },
    {
      field: "callName",
      headerName: "Dog",
      minWidth: 120,
      filter: "agTextColumnFilter",
    },
    {
      field: "armNumber",
      headerName: "Arm #",
      width: 120,
      minWidth: 120,
      maxWidth: 120,
      filter: true,
      comparator: (a: string, b: string) => {
        const numA = parseFloat(a);
        const numB = parseFloat(b);
        if (isNaN(numA) && isNaN(numB)) return 0;
        if (isNaN(numA)) return -1;
        if (isNaN(numB)) return 1;
        return numA - numB;
      },
    },
    {
      field: "showType",
      headerName: "Show Type",
      minWidth: 120,
      filter: true,
    },
    {
      field: "showName",
      headerName: "Show",
      minWidth: 120,
      width: 120,
      filter: true,
    },
    {
      field: "showDate",
      headerName: "Show Date",
      width: 150,
      minWidth: 150,
      maxWidth: 150,
      filter: true,
    },
    {
      field: "from",
      headerName: "From",
      minWidth: 150,
      filter: true,
    },
    {
      field: "to",
      headerName: "To",
      minWidth: 150,
      filter: true,
    },
    {
      field: "status",
      headerName: "Status",
      width: 110,
      minWidth: 110,
      maxWidth: 120,
      cellRenderer: StatusRenderer,
    },
    {
      field: "createdBy",
      minWidth: 250,
      width: 250,
      filter: true,
    },
    {
      headerName: "",
      field: "actions",
      maxWidth: 50,
      minWidth: 50,
      cellRenderer: ActionsRenderer,
      pinned: "right",
      resizable: true,
      cellStyle: {
        overflow: "visible",
        zIndex: "2",
      },
    },
  ];

  const defaultColDef: ColDef = {};

  const onGridReady = (params: any) => {
    setGridApi(params.api);
  };

  useEffect(() => {
    if (props.currentTab === props.tabIndex) {
      getMoveUps();
    }
  }, [props.currentTab, props.tabIndex, getMoveUps]);

  useEffect(() => {
    if (!loaded && props.currentTab === props.tabIndex) {
      getMoveUps();
      setLoaded(true);
    }
  }, [loaded, getMoveUps, props.currentTab, props.tabIndex]);

  return (
    <div className={styles.MoveUpsTab}>
      <div className={styles.actions}>
        <div className={styles.left}>
          <Input
            className={styles.textInput}
            autoComplete="off"
            type="text"
            label="Dog Name"
            value={dogNameSearch}
            onChange={(e) => {
              setDogNameSearch(e.target.value);
            }}
          />
          <Button
            disabled={!dogNameSearch}
            className={styles.reset}
            onClick={() => {
              resetFilters();
            }}
          >
            Reset
          </Button>
        </div>
      </div>
      {rowData.length === 0 && loaded && (
        <div className={styles.empty}>No move-ups recorded for this event yet.</div>
      )}
      {rowData.length > 0 && (
        <div className={styles.tableWrap}>
          <AgGridReact
            rowData={rowData}
            suppressRowTransform={true}
            columnDefs={colDefs}
            defaultColDef={defaultColDef}
            theme={theme}
            pagination={true}
            paginationPageSize={20}
            onGridReady={onGridReady}
          />
        </div>
      )}
    </div>
  );
};

export default MoveUpsTab;
